import React, { useState, useEffect } from "react";
import { Form } from "antd";
import { InputText, Select, CountryFlag } from "@comps/components";
import { getErrorProps, notify, makeRequest, NOTIFICATION_TYPE } from "@utils/helpers";
import { getClinicsReq } from "../requests";
import { COUNTRY_OPTIONS, countrySearchText } from "@utils/countries";

/*
 * The picker shows each country's flag beside its name. `label` is the
 * rendered node, `name` the plain text the select searches on — see the
 * filterOption note in BaseSelectWithSearch. It carries any older name
 * too, so typing "Turkey" still finds Türkiye.
 */
const COUNTRY_SELECT_OPTIONS = COUNTRY_OPTIONS.map(({ value }) => ({
  value,
  name: countrySearchText(value),
  label: <CountryFlag code={ value } size={ 16 } />,
}));

const UserForm = ({ preData, onFinish, initialData, formName, errors, setErrors }) => {
  // Scoped to the Clinic select. The result was previously thrown away
  // (`const [, setLoader]`), so while the clinics were in flight the field
  // sat there looking like a system with no clinics in it.
  const [clinicsLoading, setClinicsLoading] = useState(true);
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    makeRequest(setClinicsLoading, getClinicsReq, {}, onSuccess, onError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSuccess = (data) => {
    setClinics(data);
  }

  const onError = (err) => {
    // was defaulting to the success variant — a failed clinics load
    // painted itself green
    notify("Couldn't load clinics", err?.msg, NOTIFICATION_TYPE.ERROR);
  }

  return (
    <Form
      layout="vertical"
      name={ formName }
      onFinish={ onFinish }
      initialValues={ initialData }
    >
      {/* First, because everything downstream hangs off it: every case
          this user owns is counted under the country set here, and the
          admin dashboard breaks its totals down by it. */}
      <Form.Item name="country" rules={ rules.country } label="Country :" className="da-mb-16"
        { ...getErrorProps(errors['country']) }
      >
        <Select options={ COUNTRY_SELECT_OPTIONS } placeholder="Select the country they practise in" />
      </Form.Item>
      <Form.Item name="name" rules={ rules.name } label="Full Name :" className="da-mb-16"
        { ...getErrorProps(errors['name']) }
      >
        <InputText placeholder="Enter your full name" />
      </Form.Item>
      <Form.Item onChange={
        (e) => {
          setErrors([]);
        }
      } name="email" rules={ rules.email } label="Email :" className="da-mb-16"
        { ...getErrorProps(errors['email']) }
      >
        <InputText type="email" placeholder="Enter your email" />
      </Form.Item>
      <Form.Item name="password" rules={ initialData?.id ? [] : rules.password } label="Password :" className="da-mb-16"
        { ...getErrorProps(errors['password']) }
      >
        <InputText placeholder="Enter your password" />
      </Form.Item>
      <Form.Item name="clinic_id" rules={ rules.clinic_id } label="Clinic :" className="da-mb-16"
        { ...getErrorProps(errors['clinic_id']) }
      >
        <Select
          options={ clinics }
          loading={ clinicsLoading }
          disabled={ clinicsLoading }
          placeholder={ clinicsLoading ? "Loading clinics…" : "Select a clinic" }
        />
      </Form.Item>
      <Form.Item name="role_id" rules={ rules.role_id } label="Role :" className="da-mb-16"
        { ...getErrorProps(errors['role_id']) }
      >
        <Select options={ preData.roles } placeholder="Enter your role" />
      </Form.Item>
      {/*
        * "System User" used to sit here as a Yes/No select, and it could
        * not work: the server drops is_system from every create and
        * update on purpose (UserController::guardRoleAssignment — it is
        * what the whole app reads as "unrestricted", so letting a
        * request set it is a straight privilege escalation), and this
        * list only ever returns users with is_system = 0
        * (UserController::onQueryIndex). So the control was write-only
        * over a field that is always No for every record it could
        * possibly open — set it to Yes, save, reopen, and it read No
        * again, which is exactly what it looked like.
        *
        * Removed rather than made to work: the alternative is to reopen
        * the escalation the server closed deliberately. Seeded system
        * accounts stay managed where they are made.
        */}
    </Form>
  )
}


const rules = {
  name: [
    { required: true, message: 'Please input your full name!', },
    { min: 3, message: 'Minimum name length is 3', },
    { max: 100, message: 'Maximum name length is 100', },
  ],
  email: [
    { required: true, message: 'Please input your email!', },
    { min: 3, message: 'Minimum name length is 3', },
    { max: 100, message: 'Maximum name length is 100', },
  ],
  role_id: [
    { required: true, message: 'Please select role!', },
  ],
  clinic_id: [
    { required: true, message: 'Please select clinic!', },
  ],
  country: [
    { required: true, message: 'Please select country!', },
  ],
};


export default UserForm;